import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import constant from "../../constant";
import { Clock, Send, Save } from "lucide-react";

const MedicinesTiming = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const { hospitalId } = useParams();

    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    const [medicineTime, setMedicineTime] = useState({
        before_breakfast: "",
        after_breakfast: "",
        before_lunch: "",
        after_lunch: "",
        before_dinner: "",
        after_Dinner: ""
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const url = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}`;
                const res = await fetch(url, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                if (!res.ok) throw new Error("Failed to load");

                const data = await res.json();

                if (data.medicine_time) {
                    setMedicineTime({
                        before_breakfast: data.medicine_time.before_breakfast || "",
                        after_breakfast: data.medicine_time.after_breakfast || "",
                        before_lunch: data.medicine_time.before_lunch || "",
                        after_lunch: data.medicine_time.after_lunch || "",
                        before_dinner: data.medicine_time.before_dinner || "",
                        after_Dinner: data.medicine_time.after_Dinner || ""
                    });
                }
            } catch (err) {
                setError("Unable to fetch hospital details.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);


    const formatIST = (time) => {
        if (!time) return "";

        const [hours, minutes] = time.split(":");
        const date = new Date();
        date.setHours(hours, minutes, 0);

        const formatter = new Intl.DateTimeFormat("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
            timeZone: "Asia/Kolkata",
        });

        return formatter.format(date);
    };

    const handleChange = (e) => {
        setMedicineTime({
            ...medicineTime,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);

        try {
            const putUrl = `${constant.REACT_APP_API_URL}/hospital/${hospitalId}`;

            const payload = {
                medicine_time: {
                    before_breakfast: formatIST(medicineTime.before_breakfast),
                    after_breakfast: formatIST(medicineTime.after_breakfast),
                    before_lunch: formatIST(medicineTime.before_lunch),
                    after_lunch: formatIST(medicineTime.after_lunch),
                    before_dinner: formatIST(medicineTime.before_dinner),
                    after_Dinner: formatIST(medicineTime.after_Dinner),
                }
            };

            const response = await fetch(putUrl, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) throw new Error("Update failed");

            setSuccess(true);
            setTimeout(() => setSuccess(false), 2500);
        } catch (err) {
            setError("Failed to update medicine timings.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex justify-center items-center">
                <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div className="max-w-8xl mx-auto px-6 py-8">

            {/* Error */}
            {error && (
                <div className="p-4 mb-6 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}

            {/* Success */}
            {success && (
                <div className="p-4 mb-6 bg-green-50 border border-green-200 rounded-lg text-green-700">
                    Medicine timings updated successfully!
                </div>
            )}

            <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-6">Medicines Timing</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">

                {/* Breakfast */}
                <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Breakfast</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <TimeInput
                            label="Before Breakfast"
                            name="before_breakfast"
                            value={medicineTime.before_breakfast}
                            onChange={handleChange}
                        />
                        <TimeInput
                            label="After Breakfast"
                            name="after_breakfast"
                            value={medicineTime.after_breakfast}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {/* Lunch */}
                <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Lunch</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <TimeInput
                            label="Before Lunch"
                            name="before_lunch"
                            value={medicineTime.before_lunch}
                            onChange={handleChange}
                        />
                        <TimeInput
                            label="After Lunch"
                            name="after_lunch"
                            value={medicineTime.after_lunch}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {/* Dinner */}
                <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Dinner</h3>
                    <div className="grid grid-cols-2 gap-4">
                        <TimeInput
                            label="Before Dinner"
                            name="before_dinner"
                            value={medicineTime.before_dinner}
                            onChange={handleChange}
                        />
                        <TimeInput
                            label="After Dinner"
                            name="after_Dinner"
                            value={medicineTime.after_Dinner}
                            onChange={handleChange}
                        />
                    </div>
                </div>

                {/* Submit */}
                <div className="pt-6 border-t border-gray-300 flex justify-end items-center">
                    {/* <span className="text-sm text-gray-500 flex items-center gap-1">
            <Info className="h-4 w-4" /> All fields are required
          </span> */}


                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md transition-colors duration-150"
                    >
                        {isSubmitting ? (
                            <>
                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                                <span>Saving...</span>
                            </>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-1" />
                                <span>Save Timings</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

const TimeInput = ({ label, name, value, onChange }) => (
    <div className="space-y-1">
        <label className="text-sm font-medium text-gray-700">{label}</label>
        <input
            type="time"
            name={name}
            value={value}
            onChange={onChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-gray-50 hover:bg-white transition"
        />
    </div>
);

export default MedicinesTiming;
